import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import apiClient from '../services/api';
import en from './locales/en';
import ro from './locales/ro';

export const LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'ro', label: 'Română', short: 'RO' },
];

const CATALOGUES = { en, ro };

const STORAGE_PREFIX = 'avacore.lang';
const DEFAULT_LANGUAGE = 'en';

const isSupported = (code) => LANGUAGES.some((l) => l.code === code);

const storageKey = (userId) => (userId ? `${STORAGE_PREFIX}.${userId}` : STORAGE_PREFIX);

const readStoredLanguage = (userId) => {
  try {
    const scoped = userId ? localStorage.getItem(storageKey(userId)) : null;
    if (isSupported(scoped)) return scoped;
    const global = localStorage.getItem(STORAGE_PREFIX);
    if (isSupported(global)) return global;
  } catch (e) {
    // localStorage unavailable (private mode / locked-down terminal)
  }
  return DEFAULT_LANGUAGE;
};

const writeStoredLanguage = (userId, code) => {
  try {
    localStorage.setItem(STORAGE_PREFIX, code);
    if (userId) localStorage.setItem(storageKey(userId), code);
  } catch (e) {
    // ignore
  }
};

const missing = new Set();

const recordMissing = (language, text) => {
  if (process.env.NODE_ENV !== 'development') return;
  if (language === DEFAULT_LANGUAGE) return;
  if (missing.has(text)) return;
  missing.add(text);
  window.__i18nMissing = missing;
  window.__i18nDump = () =>
    Array.from(missing)
      .sort()
      .map((s) => `  ${JSON.stringify(s)}: "",`)
      .join('\n');
};

const interpolate = (text, vars) => {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (match, name) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match
  );
};

const pluralForm = (value, count) => {
  if (typeof value === 'string') return value;
  if (!value || typeof value !== 'object') return null;
  const n = Number(count);
  if (!Number.isFinite(n)) return value.other || value.one || null;
  if (n === 1) return value.one || value.other || null;
  if (n === 0 || (n % 100 >= 1 && n % 100 <= 19)) return value.few || value.other || null;
  return value.other || value.few || null;
};

export const translate = (language, text, vars) => {
  if (text === null || text === undefined) return text;
  const source = String(text);
  const catalogue = CATALOGUES[language];
  if (!catalogue) return interpolate(source, vars);

  const entry = catalogue[source];
  const resolved = pluralForm(entry, vars && vars.count);

  if (!resolved) {
    recordMissing(language, source);
    return interpolate(source, vars);
  }
  return interpolate(resolved, vars);
};

let activeLanguage = DEFAULT_LANGUAGE;

export const t = (text, vars) => translate(activeLanguage, text, vars);

const I18nContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: (text, vars) => interpolate(String(text ?? ''), vars),
  languages: LANGUAGES,
});

export const I18nProvider = ({ children }) => {
  const auth = useContext(AuthContext);
  const userId = auth?.user?.user_id || auth?.user?.id || null;
  const serverLanguage = auth?.user?.preferred_language || null;

  const [language, setLanguageState] = useState(() => readStoredLanguage(userId));

  activeLanguage = language;

  // The account is the source of truth once someone is signed in, so the
  // choice follows them onto any terminal. localStorage still backs the login
  // screen, and keeps the right language on screen during the reload before
  // the profile comes back.
  useEffect(() => {
    if (isSupported(serverLanguage)) {
      writeStoredLanguage(userId, serverLanguage);
      setLanguageState(serverLanguage);
      return;
    }
    setLanguageState(readStoredLanguage(userId));
  }, [userId, serverLanguage]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback(
    (code) => {
      if (!isSupported(code)) return;
      writeStoredLanguage(userId, code);
      setLanguageState(code);
      // Persisting is best-effort: a failure here costs the choice on the next
      // terminal, never the switch the user just made on this one.
      if (userId) {
        apiClient
          .patch('/users/', { preferred_language: code })
          .catch(() => {});
      }
    },
    [userId]
  );

  const translateFn = useCallback((text, vars) => translate(language, text, vars), [language]);

  const value = useMemo(
    () => ({ language, setLanguage, t: translateFn, languages: LANGUAGES }),
    [language, setLanguage, translateFn]
  );

  // Keying the subtree on the language remounts it on a switch. Without this a
  // component that reads the module-level `t` instead of the context keeps its
  // old strings: React skips re-rendering children whose element identity has
  // not changed, and only context consumers would update.
  return (
    <I18nContext.Provider value={value}>
      <React.Fragment key={language}>{children}</React.Fragment>
    </I18nContext.Provider>
  );
};

export const useTranslation = () => useContext(I18nContext);

export default I18nContext;
